import { ref } from "vue";
import { AddressType } from "@/core/enums";
import { isEqualAddresses } from "@/core/utilities/address";
import { useMelissaAddresses } from "@/shared/account/composables/useMelissaAddresses";
import { useUserAddresses } from "@/shared/account/composables/useUserAddresses";
import { useCheckout } from "@/shared/checkout/composables";
import { useModal } from "@/shared/modal";
import { NewAddressReturnsFormModal } from "@/shared/order";
import type { MemberAddressTypeExtension, QueryValidateMelissaAddressesArgs } from "@/core/api/graphql/types";
import AddOrUpdateAddressModal from "@/shared/account/components/add-or-update-address-modal.vue";

export function useAddressModal(showDialogCallback: () => void) {
  const { openModal, closeModal } = useModal();
  const { validateMelissaAddress } = useMelissaAddresses();
  const { addOrUpdateAddresses, addresses } = useUserAddresses();
  const suggestedAddress = ref<MemberAddressTypeExtension>();
  const userTypedInAddress = ref<MemberAddressTypeExtension>();
  const selectedAddress = ref<string>("");
  const showSuccessAlert = ref(false);
  const existingAddressEdit = ref<MemberAddressTypeExtension>();
  const { userSelectedAddress } = useCheckout();

  function showDialog() {
    // Notify the dialog to open
    showDialogCallback();
  }

  // For a brand-new address, `updatedAddress.id` is not yet assigned (it's generated by the backend on save),
  // so we resolve the persisted address by matching its field values against the freshly refetched list.
  function resolveSelectedAddressId(updatedAddress: MemberAddressTypeExtension): string | undefined {
    if (updatedAddress.id) {
      return updatedAddress.id;
    }

    return addresses.value.find((address) => isEqualAddresses(address, updatedAddress))?.id;
  }

  async function handleAddressUpdate(updatedAddress: MemberAddressTypeExtension, isEdit: boolean) {
    existingAddressEdit.value = updatedAddress;
    const args: QueryValidateMelissaAddressesArgs = {
      area: updatedAddress.regionId,
      city: updatedAddress.city,
      countryCode: updatedAddress.countryCode,
      line1: updatedAddress.line1,
      postalCode: updatedAddress.postalCode,
    };

    if (updatedAddress.countryCode === "TWN" || updatedAddress.countryCode === "CHN") {
      await addOrUpdateAddresses([{ ...updatedAddress, addressType: AddressType.BillingAndShipping }]);
      showSuccessAlert.value = !isEdit;
      closeModal();
      const selectedAddressId = resolveSelectedAddressId(updatedAddress);
      if (selectedAddressId) {
        userSelectedAddress.value = selectedAddressId;
      }
      return;
    }

    const result = await validateMelissaAddress(args);

    if (result?.success && !result?.address) {
      await addOrUpdateAddresses([{ ...updatedAddress, addressType: AddressType.BillingAndShipping }]);
    } else if (result?.success && result?.address) {
      closeModal();
      const newAddress = result?.address;

      suggestedAddress.value = { ...updatedAddress };
      suggestedAddress.value.postalCode = newAddress?.postalCode || suggestedAddress.value.postalCode;
      suggestedAddress.value.line1 = newAddress?.address || suggestedAddress.value.line1;

      userTypedInAddress.value = updatedAddress;

      selectedAddress.value = "suggestedAddress";

      showDialog();
    } else {
      await addOrUpdateAddresses([{ ...updatedAddress, addressType: AddressType.BillingAndShipping }]);
    }

    showSuccessAlert.value = !isEdit;
    const selectedAddressId = resolveSelectedAddressId(updatedAddress);
    if (selectedAddressId) {
      userSelectedAddress.value = selectedAddressId;
    }
    closeModal();
  }

  function openAddOrUpdateAddressModal(
    address?: MemberAddressTypeExtension,
    defaultBillingCheckboxHidden?: boolean,
    defaultShippingCheckboxHidden?: boolean,
  ): void {
    openModal({
      component: AddOrUpdateAddressModal,
      props: {
        address,
        defaultBillingCheckboxHidden,
        defaultShippingCheckboxHidden,
        async onResult(updatedAddress: MemberAddressTypeExtension, isEdit: boolean) {
          await handleAddressUpdate(updatedAddress, isEdit);
        },
      },
    });
  }

  function openAddAddressModalInRMAForm(address?: MemberAddressTypeExtension): void {
    openModal({
      component: NewAddressReturnsFormModal,
      props: {
        address,
        async onResult(updatedAddress: MemberAddressTypeExtension, isEdit: boolean) {
          await handleAddressUpdate(updatedAddress, isEdit);
        },
      },
    });
  }

  return {
    openAddOrUpdateAddressModal,
    openAddAddressModalInRMAForm,
    suggestedAddress,
    userTypedInAddress,
    selectedAddress,
    showSuccessAlert,
    existingAddressEdit,
    handleAddressUpdate,
  };
}
